import { useEffect, useState } from 'react'
import Layout from '@/components/Layout'
import { fetchVendors, deleteVendor } from '@/lib/api'
import type { Vendor } from '@/lib/types'

export default function VendorsList() {
  const [vendors, setVendors] = useState<Vendor[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [deleting, setDeleting] = useState<string | null>(null)

  async function load() {
    setLoading(true)
    try { setVendors(await fetchVendors()) } catch { /* noop */ }
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Delete vendor "${name}"? Their parts will also be removed.`)) return
    setDeleting(id)
    try { await deleteVendor(id); await load() } catch { /* noop */ }
    setDeleting(null)
  }

  const zoneNames: Record<string, string> = { sharjah: 'Sharjah', ajman: 'Ajman', 'al-quoz': 'Dubai · Al Quoz' }
  const filtered = vendors.filter(v =>
    !search ||
    v.name.toLowerCase().includes(search.toLowerCase()) ||
    (v.specialty || '').toLowerCase().includes(search.toLowerCase()) ||
    (v.zone || '').toLowerCase().includes(search.toLowerCase())
  )

  return (
    <Layout title="Vendors"
      action={<a href="/vendors/new" className="inline-flex items-center gap-2 bg-brand-600 text-white text-sm font-semibold px-4 py-2 rounded-xl hover:bg-brand-700 transition-colors">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
        Add Vendor
      </a>}
    >
      <div className="max-w-7xl mx-auto space-y-5">
        <div className="relative max-w-md">
          <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vendors..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-surface-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            <p className="text-slate-400 text-sm col-span-full text-center py-10">Loading...</p>
          ) : filtered.length === 0 ? (
            <p className="text-slate-400 text-sm col-span-full text-center py-10">No vendors found.</p>
          ) : filtered.map(v => (
            <div key={v.id} className="bg-white rounded-2xl border border-surface-200 shadow-card hover:shadow-card-hover transition-all p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white font-bold text-sm shadow">
                  {v.name.slice(0, 2).toUpperCase()}
                </div>
                <div className="flex items-center gap-1.5">
                  {v.verified && <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold border border-emerald-200">VERIFIED</span>}
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${v.is_active ? 'bg-slate-100 text-slate-600 border-slate-200' : 'bg-red-50 text-red-500 border-red-200'}`}>
                    {v.is_active ? 'ACTIVE' : 'HIDDEN'}
                  </span>
                </div>
              </div>
              <h3 className="font-semibold text-slate-900 text-sm leading-snug">{v.name}</h3>
              <p className="text-xs text-slate-500 mt-1">{v.specialty || '—'}</p>
              <div className="flex items-center gap-2 mt-3 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-surface-100 font-medium">{zoneNames[v.zone] ?? v.zone}</span>
                <span className="inline-flex items-center gap-1 text-amber-600 font-semibold">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                  {Number(v.rating || 0).toFixed(1)} ({v.review_count})
                </span>
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-surface-100">
                <a href={`/vendors/${v.id}`} className="text-xs font-semibold text-brand-600 hover:text-brand-700">Edit</a>
                <button onClick={() => handleDelete(v.id, v.name)} disabled={deleting === v.id}
                  className="text-xs font-semibold text-red-500 hover:text-red-600 disabled:opacity-50">
                  {deleting === v.id ? '...' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  )
}
